import APIService from '../Core/APIService';
import API_ROUTES from '../../ConfigFiles/Routes/api_routes';
import type { 
  ProOnboardingRequest, 
  UserRead 
} from '../../Types/schema-helpers';
import { RelationshipsEnum } from '../../Types/Enums/RelationshipsEnum';
import {BloodTypesEnum } from '../../Types/Enums/BloodTypesEnum';

export default class UserProOnboardingService extends APIService {
  /**
   * Transforms raw UI form state into strict ProOnboardingRequest payload.
   */
  private _mapToSchema(data: any): ProOnboardingRequest {
    const timeToMinutes = (timeValue?: any): number => {
      if (!timeValue) return 0;
      if (timeValue instanceof Date) {
        return timeValue.getHours() * 60 + timeValue.getMinutes();
      }
      if (typeof timeValue === 'number') return timeValue;
      if (typeof timeValue === 'string') {
        const cleanTime = timeValue.includes('T') ? (timeValue.split('T')[1] ?? timeValue) : timeValue;
        const parts = cleanTime.split(':');
        const hours = parseInt(parts[0] ?? '0', 10) || 0;
        const minutes = parseInt(parts[1] ?? '0', 10) || 0;
        return hours * 60 + minutes;
      }
      return 0;
    };

    // Maps weekly schedule array to { "0": [{ start, end }], ... }
    const mapWeeklySchedule = (scheduleArray: any[]): Record<string, Array<{ start: number; end: number }>> => {
      const scheduleMap: Record<string, Array<{ start: number; end: number }>> = {};
      for (let i = 0; i < 7; i++) {
        const dayIntervals = scheduleArray?.[i] || [];
        scheduleMap[i.toString()] = dayIntervals.map((slot: any) => ({
          start: timeToMinutes(slot.start),
          end: timeToMinutes(slot.end),
        }));
      }
      return scheduleMap;
    };

    const weeklySchedule = mapWeeklySchedule(data.working_hours?.weekly_schedule);

    // Extract raw skill ObjectIds (strings)
    const rawSkills = Array.isArray(data.skills?.skills)
      ? data.skills.skills.map((s: any) => (typeof s === 'string' ? s : s.id || s._id))
      : [];

    return {
      immutables: {
        blood_type: data.blood_type || BloodTypesEnum.OP,
        birthday: data.birthday ? new Date(data.birthday).toISOString() : new Date().toISOString(),
        birthplace: data.birthplace || "",
      },

      contact: {
        phone_numbers: (data.phone_numbers || []).filter((p: string) => p && String(p).trim() !== ""),
        mails: (data.mails || []).filter((m: string) => m && String(m).trim() !== ""),
      },

      emergency_contacts: (data.emergency_contacts || [])
      .filter((c: any) => c.name && c.phone)
      .map((contact: any) => ({
        name: contact.name,
        phone: contact.phone,
        relationship: Number(contact.relationship ?? RelationshipsEnum.NONE)
      })),

      addresses: [
        {
          street: data.current_address?.street || "",
          number: String(data.current_address?.number || ""),
          interior_number: data.current_address?.interior_number ? parseInt(data.current_address.interior_number, 10) : null,
          state: data.current_address?.state_id || data.current_address?.state || "",
          zip_code: data.current_address?.zip_code || "",
          google_maps_url: data.current_address?.google_maps_url || null,
          deleted: false,
        },
      ],

      professional_work_location: {
        location_name: data.professional_work_location?.location_name || "Consultorio",
        street: data.professional_work_location?.street || "",
        number: String(data.professional_work_location?.number || ""),
        state_id: data.professional_work_location?.state_id || "",
        city_id: data.professional_work_location?.city_id || null,
        zip_code: data.professional_work_location?.zip_code || "",
        country_code: data.professional_work_location?.country_code || "MX",
        location: data.professional_work_location?.location || { type: "Point", coordinates: [0, 0] },
        phone: data.professional_work_location?.phone || data.professional_work_location?.main_phone || "",
        google_maps_url: data.professional_work_location?.google_maps_url || null,
      },

      professional_assignment: {
        interior_number: data.professional_assignment?.interior_number ? parseInt(data.professional_assignment.interior_number, 10) : null,
        contact_points: [
          {
            number: data.professional_assignment?.work_number || data.professional_work_location?.phone || "",
            extension: data.professional_assignment?.work_extension ? String(data.professional_assignment.work_extension) : null,
            is_whatsapp: false,
            is_telegram: false,
            label: "Office",
          },
        ],
        manage_agenda: data.working_hours?.manage_agenda ?? true,
        slot_size: 30,
        office_hours: weeklySchedule,
        timezone: data.working_hours?.timezone || "America/Mexico_City",
      },

      professional_profile: {
        specialty: data.skills?.specialty_id || null,
        subspecialty: data.skills?.subspecialty_id || null,
        skills: rawSkills,
        qualifications: (data.qualifications || [])
          .filter((q: any) => !q.deleted)
          .map((q: any) => ({
            title: q.title || "",
            institution: q.institution || "",
            year: parseInt(q.year_of_completion, 10) || new Date().getFullYear(),
            credential_id: q.license || q.credential_id || "",
          })),
      },
    };
  }

  /**
   * Creates a professional onboarding record and returns the updated UserRead DTO.
   */
  async createProOnboarding(rawFormData: any): Promise<UserRead> {
    try {
      const mappedData = this._mapToSchema(rawFormData);
      console.log("Service: Sending ProOnboardingRequest mapped data:", mappedData);
      return await this.post<UserRead>(API_ROUTES.onboarding_pro_create, mappedData);
    } catch (error) {
      console.error("Error in createProOnboarding service:", error);
      throw error;
    }
  }

  /**
   * Upgrades an existing profile to professional onboarding and returns the updated UserRead DTO.
   */
  async upgradeProOnboarding(rawFormData: any): Promise<UserRead> {
    try {
      const mappedData = this._mapToSchema(rawFormData);
      return await this.post<UserRead>(API_ROUTES.onboarding_pro_upgrade, mappedData);
    } catch (error) {
      console.error("Error in upgradeProOnboarding service:", error);
      throw error;
    }
  }
}